import { useState } from 'react'
import { FormikProvider, useFormik } from 'formik'

import {
  UPDATE_EMAIL_KEY,
  updateUserEmail,
  VERIFY_PASSWORD_KEY,
  verifyPassword,
} from '@/actions/authActions'
import { selectErrorsForKeys } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import { getFieldName, makeReqEmailSchema, makeReqStringSchema } from '@/base'
import { Alert, Button, Dialog, Form } from '@/ui'

export const ChangeEmailDialog = () => {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)

  const errors = useReselector(selectErrorsForKeys, [
    VERIFY_PASSWORD_KEY,
    UPDATE_EMAIL_KEY,
  ])

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },

    onSubmit: async ({ email, password }) => {
      const passwordResponse = await verifyPassword({ password })(dispatch)
      if (!passwordResponse) {
        return
      }

      const emailResponse = await updateUserEmail({ newEmail: email })(dispatch)

      if (emailResponse) {
        setOpen(false)
      }
    },
  })

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <Dialog.Trigger asChild>
        <Button
          variant="secondary"
          className="w-full md:w-auto h-full"
          onClick={() => setOpen(true)}
        >
          Change email address
        </Button>
      </Dialog.Trigger>

      <Dialog.Content size="sm" onDismiss={() => setOpen(false)}>
        <Dialog.Title>Change Your Email Address</Dialog.Title>

        <Dialog.Description>
          To change your email address, enter your password and your new email
          address.
        </Dialog.Description>

        <FormikProvider value={formik}>
          {Boolean(errors.length) && (
            <Alert variant="error">{errors[0].message}</Alert>
          )}

          <Form.Input
            name={getFieldName<typeof formik.values>('password')}
            label="Password"
            type="password"
            schema={makeReqStringSchema()}
          />

          <Form.Input
            name={getFieldName<typeof formik.values>('email')}
            label="New Email"
            type="email"
            schema={makeReqEmailSchema()}
          />

          <Dialog.Footer>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={formik.submitForm}>Update Email</Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
